/*
 * Native WebSocket, no SockJS — and stompjs is still imported DYNAMICALLY.
 *
 * SockJS is gone because its module body attached `unload` and `beforeunload`
 * to window unconditionally (lib/utils/event.js), and an unload handler makes a
 * page ineligible for the back/forward cache outright. That cost showed up
 * twice: as Lighthouse `deprecations` and `bf-cache` both scoring 0, and — the
 * reason it actually mattered — as the iOS swipe-back freeze on /chat. With no
 * cached page to restore, Safari replays a screenshot of the previous entry
 * while the app boots from scratch. Every supported browser has had native
 * WebSocket for a decade; the fallback was buying nothing and costing that.
 *
 * The dynamic import stays for a separate reason: authStore imports
 * disconnectChat from this file and authStore is on every page, so a static
 * import would put the STOMP client in the bundle the landing pages download.
 * Type-only imports are erased at compile time and cost nothing — keep it that
 * way.
 *
 * The backend registers both a raw /ws endpoint and the old SockJS one at
 * /ws/** during rollout, so a browser holding the previous bundle keeps working.
 * See WebSocketConfig.registerStompEndpoints.
 */
import type { Client, IMessage, StompSubscription } from '@stomp/stompjs'
import type { ChatMessageResponse } from '@/api/chatApi'
import { useAuthStore } from '@/shared/store/authStore'

type MessageHandler = (msg: ChatMessageResponse) => void

/**
 * A frame on the signed-in member's own destination, `/topic/member.{id}`.
 *
 * Today that is only "you were added to / removed from a channel" — the two events the
 * channel topic structurally cannot carry, because in both cases the recipient is the one
 * person not subscribed to that channel. `type` is there so this destination can carry a
 * second kind of event later without a client having to guess.
 */
export type MemberEvent = {
  type: 'CHANNEL_MEMBERSHIP'
  event: 'ADDED' | 'REMOVED'
  channelId: number
  teamMemberId: number
}

type MemberEventHandler = (event: MemberEvent) => void

let stompClient: Client | null = null
/** In-flight dynamic import + activate, so a second call cannot start a second client. */
let connecting: Promise<void> | null = null
/** Bumped by disconnectChat so a connect still awaiting its import resolves into a no-op. */
let generation = 0
const subscriptions = new Map<number, StompSubscription>()
const handlers = new Map<number, Set<MessageHandler>>()
/** The signed-in member's own destination — at most one, for one member id. */
let memberSubscription: StompSubscription | null = null
let memberTopicId: number | null = null
const memberHandlers = new Set<MemberEventHandler>()
/**
 * Fired after every successful (re)connect. See `onChatConnected` — this is the
 * only signal a screen gets that it may have missed frames.
 */
const connectListeners = new Set<() => void>()

/**
 * Absolute ws:// or wss:// URL for the STOMP endpoint.
 *
 * `brokerURL` takes a full URL, unlike the SockJS factory which accepted the
 * relative '/ws'. Deriving the scheme from the page's own protocol is what keeps
 * this working in both places: plain ws on localhost, wss behind the TLS
 * terminator in staging and production. Hardcoding either one breaks the other,
 * and a ws:// socket on an https:// page is blocked as mixed content.
 */
function brokerUrl(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${scheme}//${window.location.host}/ws`
}

export function connectChat(accessToken: string): void {
  // Guarding on `stompClient || connecting` rather than the old
  // `stompClient?.connected`: the client now appears one microtask late, and
  // `connected` is false for the whole handshake, so the old guard would have
  // let a remount build a second client on top of the first.
  if (stompClient || connecting) return

  const gen = ++generation
  connecting = (async () => {
    try {
      const stomp = await import('@stomp/stompjs')
      // Logged out while the chunk was in flight — do not activate a socket
      // nobody is holding a reference to.
      if (gen !== generation) return

      const client = new stomp.Client({
        brokerURL: brokerUrl(),
        connectHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
        reconnectDelay: 5000,
        // The subscriptions died with the socket. Forget them, or onConnect would see a
        // stale entry per channel, skip re-subscribing, and the screen would stay deaf
        // after a reconnect while the socket looks healthy.
        onWebSocketClose: () => {
          subscriptions.clear()
          memberSubscription = null
        },
        onConnect: () => {
          // Re-subscribe to any channels registered before connection was ready
          handlers.forEach((_, channelId) => {
            if (!subscriptions.has(channelId)) {
              _subscribe(channelId)
            }
          })
          if (memberTopicId !== null && !memberSubscription) {
            _subscribeMember(memberTopicId)
          }
          // Re-subscribing only restores the FUTURE. Everything the server
          // published while the socket was down was delivered to nobody and is
          // never replayed, so a screen that renders from frames alone is now
          // permanently behind — silently, because the socket looks healthy.
          // Tell the listeners so they can go and re-read what they missed.
          notifyConnected()
        },
      })

      // Refresh the token before every reconnect attempt (including after
      // token rotation). Without this the auto-reconnect loop reuses the token
      // baked into connectHeaders at construction time, which may have expired
      // by the time the socket drops (laptop sleep, network switch, long session).
      client.beforeConnect = async () => {
        if (gen !== generation) return  // logged-out — deactivate already called
        const token = useAuthStore.getState().accessToken
        if (token != null) {
          client.connectHeaders = { Authorization: `Bearer ${token}` }
        }
      }

      stompClient = client
      client.activate()
    } finally {
      connecting = null
    }
  })()
}

export function disconnectChat(): void {
  generation++
  connecting = null
  subscriptions.forEach((sub) => safeUnsubscribe(sub))
  subscriptions.clear()
  handlers.clear()
  safeUnsubscribe(memberSubscription)
  memberSubscription = null
  memberTopicId = null
  memberHandlers.clear()
  stompClient?.deactivate()
  stompClient = null
}

/**
 * UNSUBSCRIBE is a frame like any other: stompjs writes it to the socket unconditionally, and
 * a socket that is CLOSING or CLOSED (the server dropped it, or it is mid-reconnect) answers
 * with a console error — which the e2e console guard reports, and a user's devtools show.
 * The subscription died with the socket anyway, so there is nothing to tell the broker.
 */
function safeUnsubscribe(sub: StompSubscription | null | undefined): void {
  if (!sub) return
  if (!stompClient?.connected || stompClient.webSocket?.readyState !== WebSocket.OPEN) return
  try {
    sub.unsubscribe()
  } catch {
    // socket went away between the check and the send — same outcome as above
  }
}

function notifyConnected(): void {
  connectListeners.forEach((listener) => {
    try {
      listener()
    } catch {
      // A listener that throws must not abort the remaining ones, nor the
      // subscribe pass that just ran above it.
    }
  })
}

/**
 * Register a callback for every successful (re)connect of the STOMP client.
 *
 * A socket-only drop — flaky wifi at a shoot, a laptop lid, a proxy timeout —
 * never fires the browser's own `online`/`offline` or `focus` events, so
 * TanStack Query's `refetchOnReconnect` and `refetchOnWindowFocus` do not fire
 * either: from the browser's point of view nothing happened. Without this hook
 * the only thing that ever refills the gap is a full page reload, which nothing
 * in the product asks the user to do (finding C2-3, round p3-tasks-chat-C2).
 *
 * Deliberately not a connection-status API: it reports "you may have missed
 * something, go and re-read", not "you are offline". Showing the user a
 * connection indicator is a product decision, not a cache one.
 *
 * Fires on the FIRST connect too. Callers must therefore be idempotent — a
 * refetch is.
 *
 * Returns an unsubscribe function; call it on unmount.
 */
export function onChatConnected(listener: () => void): () => void {
  connectListeners.add(listener)
  return () => {
    connectListeners.delete(listener)
  }
}

function _subscribe(channelId: number): void {
  if (!stompClient?.connected) return
  const sub = stompClient.subscribe(`/topic/channel.${channelId}`, (frame: IMessage) => {
    try {
      const msg: ChatMessageResponse = JSON.parse(frame.body)
      handlers.get(channelId)?.forEach((h) => h(msg))
    } catch {
      // malformed frame — ignore
    }
  })
  subscriptions.set(channelId, sub)
}

function _subscribeMember(memberId: number): void {
  if (!stompClient?.connected) return
  memberSubscription = stompClient.subscribe(`/topic/member.${memberId}`, (frame: IMessage) => {
    try {
      const event: MemberEvent = JSON.parse(frame.body)
      memberHandlers.forEach((h) => h(event))
    } catch {
      // malformed frame — ignore
    }
  })
}

/**
 * Listen on the signed-in member's own destination.
 *
 * The server authorizes this subscription by identity — the id in the destination must be
 * the subscriber's own — so passing anybody else's id gets you nothing but a denied
 * SUBSCRIBE. Returns an unsubscribe function; call it on unmount.
 */
export function subscribeToMemberEvents(memberId: number, handler: MemberEventHandler): () => void {
  memberHandlers.add(handler)
  if (memberTopicId !== memberId) {
    // A different member is signed in than the one we are (or were) listening for.
    safeUnsubscribe(memberSubscription)
    memberSubscription = null
    memberTopicId = memberId
  }
  if (stompClient?.connected && !memberSubscription) {
    _subscribeMember(memberId)
  }
  return () => {
    memberHandlers.delete(handler)
    if (memberHandlers.size === 0) {
      safeUnsubscribe(memberSubscription)
      memberSubscription = null
      memberTopicId = null
    }
  }
}

export function subscribeToChannel(channelId: number, handler: MessageHandler): () => void {
  if (!handlers.has(channelId)) {
    handlers.set(channelId, new Set())
  }
  handlers.get(channelId)!.add(handler)

  if (stompClient?.connected && !subscriptions.has(channelId)) {
    _subscribe(channelId)
  }

  return () => {
    handlers.get(channelId)?.delete(handler)
    if (handlers.get(channelId)?.size === 0) {
      safeUnsubscribe(subscriptions.get(channelId))
      subscriptions.delete(channelId)
      handlers.delete(channelId)
    }
  }
}
